import React, { useState } from 'react';
import { Box, Center, Image, Spinner, useDisclosure } from '@chakra-ui/react';
import { keyframes } from '@emotion/react';
import { Blurhash } from 'react-blurhash';
import { unwrapModel } from '../../../services/api/unwrapModel';
import { PhotoSize } from '../../../types/enums/PhotoSize';
import usePolling from '../../../hooks/usePolling';
import { getLoadedImageUrlForSize, storeLoadedImageUrlForSize, } from '../../../services/photos/photoBySizeCache';
import { useTokens } from '../../../context/TokenProvider';
import BinButton from '../FindingsActionMenu/EditFinding/BinButton';
import { RenderIf } from '../RenderIf/RenderIf';
import DeletePhotoModal from './DeletePhotoModal';
import { getPhotoBySize } from '../../../services/api/generated/client';
const Photo = ({ photo, size = PhotoSize.ORIGINAL, shouldLoad, onPhotoDeleted, loadedPhoto, canDelete, query = (signal) => getPhotoBySize(photo.id, size, { signal }), spinnerProps, ...rest }) => {
    const tokens = useTokens();
    const deleteConfirmationModal = useDisclosure();
    const providedLoadedOrCached = loadedPhoto ?? getLoadedImageUrlForSize(photo.id, size);
    const [imageLoaded, setImageLoaded] = useState(false);
    const [hasError, setHasError] = useState(false);
    const { data: pollData, isPolling } = usePolling({
        interval: 3000,
        maxAttempts: providedLoadedOrCached ? 0 : 3,
        firstRunDelay: 0,
        enabled: !providedLoadedOrCached && shouldLoad,
        query,
        onPollFailure: () => {
            setHasError(true);
        },
        validator: (response) => {
            const url = unwrapModel(response);
            return !!url;
        },
        onComplete: (response) => {
            const url = unwrapModel(response);
            if (!url)
                return;
            storeLoadedImageUrlForSize(photo.id, size, url);
        },
    });
    const photoUrl = providedLoadedOrCached?.url ?? unwrapModel(pollData);
    if (!photo.blur_hash && isPolling && !photoUrl) {
        return (React.createElement(Center, { w: "100%", h: "100%" },
            React.createElement(Spinner, { ...spinnerProps })));
    }
    const unavailableImageUrl = `/images/photo-unavailable-${size}.svg`;
    const pulse = keyframes `
    0% { opacity: 1; }
    50% { opacity: 0.6; }
    100% { opacity: 1; }
  `;
    return (React.createElement(React.Fragment, null,
        React.createElement(Center, { position: "relative", width: "100%", height: "100%" },
            photo.blur_hash && !hasError && (React.createElement(Box, { borderRadius: tokens.BorderRadius02, overflow: "hidden", position: "absolute", top: 0, "data-testid": "photo_blur_hash", left: 0, right: 0, animation: imageLoaded ? undefined : `${pulse} 1.5s ease-in-out infinite`, bottom: 0, zIndex: 0 },
                React.createElement(Blurhash, { hash: photo.blur_hash, width: "100%", height: "100%" }))),
            React.createElement(Image, { src: hasError ? unavailableImageUrl : photoUrl, fallbackSrc: unavailableImageUrl, onError: () => {
                    setHasError(true);
                }, onLoad: () => setImageLoaded(true), opacity: imageLoaded || hasError || !photo.blur_hash ? 1 : 0, transition: providedLoadedOrCached ? undefined : 'opacity 0.4s ease-in-out', position: "relative", loading: "lazy", zIndex: 1, w: "100%", h: "100%", objectFit: "cover", "data-testid": "photo-image", ...rest }),
            React.createElement(RenderIf, { condition: !!canDelete && imageLoaded },
                React.createElement(BinButton, { position: "absolute", bottom: 1, right: 1, zIndex: 2, "data-testid": "photo_bin_button", "aria-label": "Delete image", onClick: (e) => {
                        e.stopPropagation();
                        deleteConfirmationModal.onOpen();
                    }, disabled: deleteConfirmationModal.isOpen }))),
        React.createElement(RenderIf, { condition: deleteConfirmationModal.isOpen },
            React.createElement(DeletePhotoModal, { onDeleted: () => {
                    deleteConfirmationModal.onClose();
                    onPhotoDeleted?.();
                }, photo: photo, onClose: deleteConfirmationModal.onClose }))));
};
export default React.memo(Photo);
